import "../styles/print.css";

/**
 * 날짜 문자열을 "YYYY년 M월 D일" 형식으로 포맷합니다.
 */
const formatDateKo = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

const TYPE_LABEL = {
  SINGLE_CHOICE: "단일 선택",
  MULTIPLE_CHOICE: "복수 선택",
  TEXT: "주관식",
};

// ─── 바 차트 행 ──────────────────────────────────────────────────
const PrintBarRow = ({ option }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 5, gap: 8 }}>
    <div style={{ width: "28%", fontSize: 10, lineHeight: 1.4, flexShrink: 0, wordBreak: "keep-all" }}>
      {option.content}
    </div>
    <div className="print-bar-track" style={{ flex: 1, height: 10, borderRadius: 3, overflow: "hidden", backgroundColor: "#eee" }}>
      <div className="print-bar-fill" style={{ width: `${option.percentage}%`, height: "100%", backgroundColor: "#444", borderRadius: 3 }} />
    </div>
    <div style={{ width: 80, fontSize: 10, textAlign: "right", flexShrink: 0, fontWeight: 700 }}>
      {option.count}명&nbsp;({option.percentage}%)
    </div>
  </div>
);

// ─── 질문 블록 ────────────────────────────────────────────────────
const PrintQuestion = ({ question, index }) => {
  const hasOptions = question.options?.length > 0;
  const hasText = question.text_answers?.length > 0;
  const typeLabel = TYPE_LABEL[question.question_type] ?? "기타";

  return (
    <div className="print-question" style={{ marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #e0e0e0" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#555", minWidth: 28, flexShrink: 0, paddingTop: 1 }}>
          Q{index + 1}.
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.5, marginBottom: 4, wordBreak: "keep-all", whiteSpace: "pre-wrap" }}>
            {question.content}
          </div>
          <div style={{ fontSize: 10, color: "#666", display: "flex", gap: 8 }}>
            <span>[{typeLabel}]</span>
            <span>응답 {question.total_responses}건</span>
            {question.average_score != null && (
              <span style={{ fontWeight: 700 }}>평균 {question.average_score}점</span>
            )}
          </div>
        </div>
      </div>

      {hasOptions && (
        <div style={{ paddingLeft: 36 }}>
          {question.options.map((opt, i) => (
            <PrintBarRow key={i} option={opt} />
          ))}
        </div>
      )}

      {hasText && (
        <div style={{ paddingLeft: 36, marginTop: 6 }}>
          {question.text_answers.map((ans, i) => (
            <div key={i} className="print-text-answer" style={{
              padding: "5px 10px",
              backgroundColor: "#f9f9f9",
              borderLeft: "3px solid #666",
              marginBottom: 4,
              fontSize: 10,
              lineHeight: 1.6,
              wordBreak: "keep-all",
              whiteSpace: "pre-wrap",
            }}>
              {typeof ans === "string" ? ans : ans.answer ?? JSON.stringify(ans)}
            </div>
          ))}
        </div>
      )}

      {!hasOptions && !hasText && (
        <div style={{ paddingLeft: 36, fontSize: 10, color: "#999", fontStyle: "italic" }}>
          제출된 답변이 없습니다.
        </div>
      )}
    </div>
  );
};

// ─── 카테고리 섹션 ────────────────────────────────────────────────
const PrintCategory = ({ category, questions }) => (
  <div className="print-category" style={{ marginBottom: 20 }}>
    <div className="print-category-header" style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 12px",
      backgroundColor: "#f5f5f5",
      borderLeft: "4px solid #444",
      marginBottom: 12,
    }}>
      <span style={{ fontSize: 13, fontWeight: 800 }}>{category}</span>
      <span style={{ fontSize: 10, color: "#666" }}>{questions.length}개 질문</span>
    </div>
    <div style={{ paddingLeft: 8 }}>
      {questions.map((q, i) => (
        <PrintQuestion key={q.question_id} question={q} index={i} />
      ))}
    </div>
  </div>
);

// ─── 기간 섹션 ────────────────────────────────────────────────────
const groupByCategory = (questions) => {
  const map = {};
  const order = [];
  (questions ?? []).forEach((q) => {
    const cat = q.category ?? "기타";
    if (!map[cat]) { map[cat] = []; order.push(cat); }
    map[cat].push(q);
  });
  return order.map((cat) => ({ category: cat, questions: map[cat] }));
};

const PrintPeriod = ({ periodName, totalParticipants, questions }) => {
  const grouped = groupByCategory(questions);
  return (
    <div className="print-period">
      <div className="print-period-header" style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        backgroundColor: "#333",
        color: "#fff",
        marginBottom: 20,
      }}>
        <span style={{ fontSize: 15, fontWeight: 800 }}>{periodName}</span>
        <span style={{ fontSize: 11 }}>응답자 {totalParticipants}명</span>
      </div>
      {grouped.map(({ category, questions: qs }) => (
        <PrintCategory key={category} category={category} questions={qs} />
      ))}
    </div>
  );
};

// ─── 표지 ─────────────────────────────────────────────────────────
/**
 * @param {string} periodName - 출력할 기간명 ("종합" | "단기" | "중기" | "장기")
 */
const PrintCover = ({ stats, periodName }) => {
  const today = new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const dateRange =
    stats.start_date || stats.end_date
      ? [formatDateKo(stats.start_date), formatDateKo(stats.end_date)]
          .filter(Boolean)
          .join(" ~ ")
      : null;

  return (
    <div className="print-cover" style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      padding: "40px 60px",
      gap: 0,
    }}>
      <div className="print-cover-accent" style={{ width: "100%", height: 6, backgroundColor: "#333", marginBottom: 60 }} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 14 }}>
        <div style={{ fontSize: 14, letterSpacing: 4, color: "#555", marginBottom: 4 }}>
          월성복지관
        </div>
        <div style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.4, marginBottom: 4 }}>
          청년도전지원<br />만족도 조사
        </div>
        <div className="print-cover-title-sub" style={{ fontSize: 20, fontWeight: 700, letterSpacing: 6, color: "#444", marginBottom: 12 }}>
          통 계 보 고 서
        </div>

        {/* 기간 배지 — 어떤 기간 통계인지 명확히 표시 */}
        <div style={{
          display: "inline-block",
          padding: "6px 24px",
          border: "2px solid #333",
          borderRadius: 4,
          fontSize: 15,
          fontWeight: 800,
          letterSpacing: 2,
          marginBottom: 20,
        }}>
          {periodName} 통계
        </div>

        {/* 구분선 */}
        <div style={{ width: 60, height: 2, backgroundColor: "#555", margin: "0 auto 24px" }} />

        {/* 메타 정보 */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          {dateRange && (
            <div style={{ fontSize: 13 }}>
              수집 기간&nbsp;<strong>{dateRange}</strong>
            </div>
          )}
          <div style={{ fontSize: 13 }}>
            전체 응답자&nbsp;<strong>{stats.total_participants}명</strong>
          </div>
          {stats.by_period?.map((p) => (
            <div key={p.period} style={{ fontSize: 12, color: "#555" }}>
              {p.period}: {p.total_participants}명
            </div>
          ))}
          <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
            출력일: {today}
          </div>
        </div>
      </div>

      <div className="print-cover-accent" style={{ width: "100%", height: 4, backgroundColor: "#333", marginTop: 60 }} />
    </div>
  );
};

// ─── 메인 컴포넌트 ────────────────────────────────────────────────

/**
 * 인쇄 전용 통계 보고서 컴포넌트
 * - 화면에서는 display: none (@media screen)
 * - window.print() 호출 시 visibility: visible로 단독 출력
 *
 * 현재 활성화된 탭의 기간 데이터만 출력합니다:
 *   - 종합 탭 → 종합 통계만
 *   - 단기 탭 → 단기 통계만
 *   - 중기/장기 탭도 동일
 *
 * @param {object} stats       - API 응답 원본
 * @param {string} periodName  - 현재 탭 기간명 ("종합" | "단기" | "중기" | "장기")
 * @param {object} periodData  - 현재 탭 데이터 (total_participants, questions)
 */
const SurveyPrintDocument = ({ stats, periodName, periodData }) => {
  if (!stats || !periodData) return null;

  return (
    <div
      className="survey-print-root"
      style={{
        fontFamily: "'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
        fontSize: 12,
        lineHeight: 1.6,
        color: "#000",
        backgroundColor: "#fff",
        width: "100%",
      }}
    >
      {/* 표지 (기간명 포함) */}
      <PrintCover stats={stats} periodName={periodName} />

      {/* 현재 탭의 기간 섹션만 출력 */}
      <PrintPeriod
        periodName={periodName}
        totalParticipants={periodData.total_participants}
        questions={periodData.questions ?? []}
      />
    </div>
  );
};

export default SurveyPrintDocument;
